import {useEffect, useMemo, useRef, useState} from 'react';
import {Popup, useMap, useMapEvents} from 'react-leaflet';
import {Marker as LeafletMarker} from 'leaflet';
import {CheckOutlined, DeleteOutlined, DragOutlined} from '@ant-design/icons';
import {Button, Col, Divider, Row, Typography} from 'antd';
import {useTranslation} from 'react-i18next';
import {capitalize} from 'lodash';
import {useEditLocationContext} from '../../../context/editAsset/EditAssetLocationContext';
import {useAssetDetails} from '../../../context/assetDetails/AssetDetails';
import {CustomMarker} from '../../common/map/CustomMarker';
import {useControlsAction} from '../../../context/baseMap/ControlContext';
import {ASSET_MAP_ACTIONS} from '../../common/map/constants/map';
import {usePatchAssetMutation} from '../../../redux/api/assets';
import {useEditAssetDetailsContext} from "../../../context/editAsset/EditAssetDetailsContext";
import {useEditAssetRealEstateContext} from "../../../context/editAsset/EditAssetRealEstateFormContext";
import {useMessageContext} from "../../../context/message/MessageContext";

const {Paragraph, Title} = Typography;
export const EditAssetPositionMarker = () => {
  const [patchAsset] = usePatchAssetMutation();

  const setAntdMessage = useMessageContext()
  const [realEstateDetails] = useEditAssetRealEstateContext();
  const [details] = useEditAssetDetailsContext();
  const {
    action: {
      currentAction,
      actionEvent,
    },
  } = useControlsAction();

  const markerRef = useRef<LeafletMarker | null>(null);

  const {t} = useTranslation();
  const map = useMap();

  const [
    location,
    setLocation,
  ] = useEditLocationContext();

  const [, {asset}] = useAssetDetails();

  const [
    isMarkerDraggable,
    setIsMarkerDraggable,
  ] = useState(false);

  const [
    coordinates,
    setCoordinates,
  ] = useState<[number, number] | null>(null);

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        if (markerRef.current) {
          setCoordinates([markerRef.current.getLatLng().lat, markerRef.current.getLatLng().lng]);
        }
      },
      contextmenu() {
        markerRef.current?.openPopup();
      },
    }),
    [],
  );

  useMapEvents({
    dblclick(e) {
      if (!location.position) {
        setLocation(location => (
          {
            ...location,
            position: [e.latlng.lat, e.latlng.lng],
          }));
      }
    },
  });

  useEffect(() => {
    if (location.position) {
      setCoordinates(location.position);
      map.setView(location.position, 18);
    }
  }, [location.position, map]);

  useEffect(() => {
    switch (currentAction) {
      case ASSET_MAP_ACTIONS.SEARCH_LOCATION:
        const newLat = actionEvent.position.lat;
        const newLng = actionEvent.position.lng;
        const newCoords: [number, number] = [newLat, newLng];

        setCoordinates(newCoords);

        // quando asset non ha le coordinate --> alla prima ricerca faccio la patch della posizione cercata
        if (!location.position && details.sigi_code) {
          setLocation(prev => ({
            ...prev,
            position: newCoords,
          }));

          patchAsset({
            uuid: asset.uuid,
            patch: {
              ...details,
              ...location, // Verrà sovrascritto da position sotto
              ...realEstateDetails,
              sigi_code: details.sigi_code,
              position: {lat: newLat, lng: newLng},
              opening_date: details?.opening_date?.format('YYYY-MM-DD') || null,
              closing_date: details?.closing_date?.format('YYYY-MM-DD') || null,
              realestate_hub: realEstateDetails.realestate_hub ? realEstateDetails.realestate_hub.value : null,
              contact_people: realEstateDetails.contact_people.map(person => person.value),
              companies: details.companies.map(company => company.value),
              master_asset: details.master_asset ? details.master_asset.value : null,
            },
          });
        }
        break;
      default:
        break;
    }
  }, [
    details,
    realEstateDetails,
    currentAction,
    actionEvent,
    location,
    location.position,
    details.sigi_code,
    asset.uuid,
    patchAsset,
    setLocation
  ]);

  return (
    <>
      {coordinates && (
        <CustomMarker
          position={coordinates}
          eventHandlers={eventHandlers}
          ref={markerRef}
          draggable={isMarkerDraggable}
        >
          <Popup className={'asset-position-preview-marker-popup'}>
            <>
              <Row>
                <Col>
                  <Title level={4}>{asset.sigi_code}</Title>
                </Col>
              </Row>
              <Row>
                <Col flex={1}>
                  <Paragraph type={'secondary'}>
                    {capitalize(t('misc.buildingHasBeenPlacedHere'))}.
                  </Paragraph>
                </Col>
              </Row>
              <Divider/>
              <Row justify={'center'} gutter={[16, 16]}>
                <Col>
                  {!isMarkerDraggable && (
                    <Button
                      type={'default'}
                      disabled={isMarkerDraggable}
                      onClick={() => {
                        setIsMarkerDraggable(draggable => !draggable);
                        map.closePopup();
                      }}
                      icon={<DragOutlined/>}
                    >
                      {capitalize(t('misc.correctPosition'))}
                    </Button>)}
                  {isMarkerDraggable && (
                    <Button
                      type={'primary'}
                      disabled={!isMarkerDraggable}
                      onClick={() => {
                        setIsMarkerDraggable(draggable => !draggable);
                        map.closePopup();
                        setLocation(location => (
                          {
                            ...location,
                            position: coordinates,
                          }));
                        if (details.sigi_code) {
                          try {
                            patchAsset({
                              uuid: asset.uuid,
                              patch: {
                                ...details,
                                ...location,
                                ...realEstateDetails,
                                sigi_code: details.sigi_code,
                                position: {lat: coordinates[0], lng: coordinates[1]},
                                opening_date: details?.opening_date?.format('YYYY-MM-DD') || null,
                                closing_date: details?.closing_date?.format('YYYY-MM-DD') || null,
                                realestate_hub: realEstateDetails.realestate_hub ? realEstateDetails.realestate_hub.value : null,
                                contact_people: realEstateDetails.contact_people.map(person => person.value),
                                companies: details.companies.map(company => company.value),
                                master_asset: details.master_asset ? details.master_asset.value : null,
                              },
                            });
                            setAntdMessage({success: capitalize(t('misc.positionSaved'))});
                          } catch (e) {
                            console.error('Error saving asset position:', e);
                            setAntdMessage({error: capitalize(t('misc.positionSaveError'))});
                          }
                        }
                      }}
                      icon={<CheckOutlined/>}
                    >
                      {capitalize(t('misc.confirmPosition'))}
                    </Button>)}
                </Col>
                <Col>
                  <Button
                    type={'default'}
                    danger={true}
                    onClick={() => {
                      setCoordinates(null);
                      setLocation(location => (
                        {
                          ...location,
                          position: null,
                        }));
                      map.closePopup();
                    }}
                    icon={<DeleteOutlined/>}
                  >
                    {capitalize(t('form.delete'))}
                  </Button>
                </Col>
              </Row>
            </>
          </Popup>
        </CustomMarker>
      )}
    </>
  );
};